import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ClipboardList, Search } from "lucide-react";
import { formatPrice, type Order, type OrderStatus } from "@nh/shared";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Button, Input, Select } from "@/shared/ui/core";
import { EmptyState, SkeletonTable } from "@/shared/ui/ui";
import {
  AdminGuard,
  DataTable,
  DataTableFrame,
  FilterBar,
  Pagination,
  Td,
  Th,
  Tr,
  DEFAULT_PER_PAGE,
} from "@/features/admin/components";
import { useAdminOrders } from "./orders.queries";
import { OrderStatusBadge, useOrderStatusOptions } from "./OrderStatusBadge";
import { formatOrderDate } from "./format";

/** Cuántas órdenes por página. El mismo tamaño que el catálogo. */

/**
 * Órdenes: los pedidos de la tienda, replicando el `view-orders` de
 * producción. Hoy los datos vienen del seed o del alta por el API; el
 * día que exista el checkout, de él — y esta pantalla no cambia.
 */
export function AdminOrdersPage() {
  return (
    <AdminGuard>
      <OrdersScreen />
    </AdminGuard>
  );
}

/** Cuántos artículos suma el pedido, contando cantidades. */
function unidadesDe(order: Order): number {
  return order.items.reduce((sum, item) => sum + item.quantity, 0);
}

function OrdersScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  /*
   * Las filas por página las elige quien mira (Anahí, 29-ago): 5, 10 o 20,
   * con CINCO por defecto. Vive en el estado y no como constante porque
   * ahora cambia en caliente; al cambiarlo se vuelve a la página 1, que es
   * lo único sensato — la página 4 de 5 en 5 no es la página 4 de 20 en 20.
   */
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [minTotal, setMinTotal] = useState("");
  const statusOptions = useOrderStatusOptions();

  /*
   * Los filtros VIAJAN en la consulta (Anahí, 27-ago): el servidor
   * filtra y pagina; aquí nunca llega más que la página visible.
   */
  const orders = useAdminOrders({
    page,
    perPage: perPage,
    ...(search.trim() ? { query: search.trim() } : {}),
    ...(status ? { status: status as OrderStatus } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(minTotal !== "" && Number.isFinite(Number(minTotal))
      ? { minTotal: Number(minTotal) }
      : {}),
  });
  const items = orders.data?.items ?? [];
  const total = orders.data?.total ?? 0;

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Órdenes")}
        description={t("{{total}} órdenes registradas.", { total })}
      />

      <DataTableFrame aria-busy={orders.isFetching}>
        {/* ── Los filtros, dentro de la card como en Productos ────────── */}
        <FilterBar>
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Buscar")}
            <div className="relative w-64">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-ink-3" />
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Folio, cliente o correo…")}
                className="pl-10"
              />
            </div>
          </label>
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Estado")}
            <div className="w-40">
              <Select
                aria-label={t("Estado")}
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Todos")}
                options={statusOptions}
              />
            </div>
          </label>
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Desde")}
            <Input
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Hasta")}
            <Input
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Monto mínimo")}
            <div className="w-32">
              <Input
                type="number"
                min={0}
                value={minTotal}
                onChange={(event) => {
                  setMinTotal(event.target.value);
                  setPage(1);
                }}
                placeholder="$0"
              />
            </div>
          </label>
        </FilterBar>
        {orders.isPending ? (
          <div className="p-5" aria-busy="true" aria-live="polite">
            <span className="sr-only">{t("Cargando las órdenes…")}</span>
            <SkeletonTable rows={6} columns={6} />
          </div>
        ) : orders.isError ? (
          <div className="p-4">
            <Alert variant="error" title={t("No se pudieron cargar las órdenes")}>
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => void orders.refetch()}
              >
                {t("Reintentar")}
              </Button>
            </Alert>
          </div>
        ) : items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={<ClipboardList className="h-5 w-5 text-ds-ink-3" />}
              title={t("Todavía no hay órdenes")}
              description={t(
                "Siembra datos de prueba con `npm run seed:orders -w backend`, o espera al checkout.",
              )}
            />
          </div>
        ) : (
          <DataTable caption={t("Órdenes de la tienda")}>
            <thead>
              <tr>
                <Th>{t("Folio")}</Th>
                <Th>{t("Cliente")}</Th>
                <Th align="right">{t("Artículos")}</Th>
                <Th align="right">{t("Total")}</Th>
                <Th align="center">{t("Estado")}</Th>
                <Th align="right">{t("Fecha")}</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((order) => (
                /*
                  La FILA entera lleva al detalle (Anahí, 27-ago): la
                  celda exacta donde caiga el clic no debería importar.
                  El folio conserva su enlace de verdad — clic medio,
                  copiar dirección y teclado siguen funcionando por él.
                */
                <Tr
                  key={order.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/admin/orders/${order.id}`)}
                >
                  <Td className="font-mono text-[13px]">
                    <Link
                      to={`/admin/orders/${order.id}`}
                      className="text-ds-violet-pop underline-offset-2 hover:underline"
                    >
                      {order.folio}
                    </Link>
                  </Td>
                  <Td>
                    <p className="font-medium">{order.customer.name}</p>
                    <p className="text-[12px] text-ds-ink-3">{order.customer.email}</p>
                  </Td>
                  <Td align="right">{unidadesDe(order)}</Td>
                  <Td align="right" className="font-medium">
                    {formatPrice(order.totalCents, order.currency)}
                  </Td>
                  <Td align="center">
                    <OrderStatusBadge status={order.status} />
                  </Td>
                  <Td align="right" className="text-ds-ink-3">
                    {formatOrderDate(order.createdAt)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </DataTable>
        )}

        {total > 0 && (
          <Pagination
            page={page}
            perPage={perPage}
            total={total}
            onPageChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        )}
      </DataTableFrame>
    </>
  );
}
